import React from 'react';

interface SkeletonProps {
  variant?: 'text' | 'circular' | 'rectangular' | 'card';
  width?: string | number;
  height?: string | number;
  className?: string;
  count?: number;
}

const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'text',
  width,
  height,
  className = '',
  count = 1,
}) => {
  const getStyles = (): React.CSSProperties => {
    const base: React.CSSProperties = { width, height };
    switch (variant) {
      case 'circular':    return { ...base, height: height || 40, width: width || 40, borderRadius: '50%' };
      case 'rectangular': return { ...base, height: height || 100, borderRadius: 8 };
      case 'card':        return { ...base, height: height || 120, borderRadius: 10 };
      default:            return { ...base, height: height || 14, borderRadius: 4 };
    }
  };

  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className={`sk-shimmer ${className}`} style={getStyles()} />
      ))}
    </>
  );
};

/* ── Stat card skeleton — mirrors db-stat card shape ── */
export const StatCardSkeleton: React.FC = () => (
  <div className="db-stat-skeleton">
    <div className="db-stat-skeleton__top-bar sk-shimmer" />
    <div className="db-stat-skeleton__body">
      <div className="db-stat-skeleton__icon sk-shimmer" />
      <div className="db-stat-skeleton__info">
        <div className="sk-shimmer db-stat-skeleton__label" />
        <div className="sk-shimmer db-stat-skeleton__value" />
      </div>
    </div>
    <div className="db-stat-skeleton__footer">
      <div className="sk-shimmer db-stat-skeleton__trend" />
    </div>
  </div>
);

/* ── Table row skeleton — mirrors db-table__row shape ── */
export const TableRowSkeleton: React.FC<{ columns?: number }> = () => (
  <div className="db-row-skeleton">
    <div className="db-row-skeleton__cell db-row-skeleton__cell--client">
      <div className="sk-shimmer db-row-skeleton__avatar" />
      <div className="db-row-skeleton__lines">
        <div className="sk-shimmer db-row-skeleton__line db-row-skeleton__line--name" />
      </div>
    </div>
    <div className="db-row-skeleton__cell">
      <div className="sk-shimmer db-row-skeleton__line db-row-skeleton__line--id" />
    </div>
    <div className="db-row-skeleton__cell">
      <div className="sk-shimmer db-row-skeleton__line db-row-skeleton__line--date" />
    </div>
    <div className="db-row-skeleton__cell">
      <div className="sk-shimmer db-row-skeleton__line db-row-skeleton__line--num" />
    </div>
    <div className="db-row-skeleton__cell">
      <div className="sk-shimmer db-row-skeleton__line db-row-skeleton__line--num" />
    </div>
    <div className="db-row-skeleton__cell">
      <div className="sk-shimmer db-row-skeleton__badge" />
    </div>
    <div className="db-row-skeleton__cell db-row-skeleton__cell--action">
      <div className="sk-shimmer db-row-skeleton__icon" />
    </div>
  </div>
);

export const FormFieldSkeleton: React.FC = () => (
  <div className="form-group">
    <Skeleton width="30%" height={14} />
    <Skeleton width="100%" height={40} />
  </div>
);

export const NavItemSkeleton: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px' }}>
    <Skeleton variant="rectangular" width={20} height={20} />
    <Skeleton width="70%" height={14} />
  </div>
);

export default Skeleton;
